import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useMemo, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InternJobsTimeSeriesChartQuery } from "./__generated__/InternJobsTimeSeriesChartQuery.graphql";
import { useColorMode } from "../../../contexts/ColorModeContext";

const timeSeriesQuery = graphql`
  query InternJobsTimeSeriesChartQuery(
    $brandId: String
    $taskName: String
    $hours: Int!
    $granularity: String!
  ) {
    internJobTimeSeries(
      brandId: $brandId
      taskName: $taskName
      hours: $hours
      granularity: $granularity
    ) {
      timestamp
      totalJobs
      successCount
      failureCount
      runningCount
      successRate
    }
  }
`;

// Extract short task name from full path
const getShortTaskName = (fullName: string): string => {
  const parts = fullName.split(".");
  return parts[parts.length - 1];
};

interface TaskNameOption {
  readonly taskName: string;
  readonly count: number;
}

interface InternJobsTimeSeriesChartProps {
  brandId: string;
  taskNames: readonly TaskNameOption[];
}

const InternJobsTimeSeriesChart = ({
  brandId,
  taskNames,
}: InternJobsTimeSeriesChartProps) => {
  const { colorMode } = useColorMode();
  const [timeRange, setTimeRange] = useState<"24h" | "7d">("24h");
  const [taskFilter, setTaskFilter] = useState<string>("");

  const hours = timeRange === "24h" ? 24 : 168;
  const granularity = timeRange === "24h" ? "hour" : "day";

  const data = useLazyLoadQuery<InternJobsTimeSeriesChartQuery>(
    timeSeriesQuery,
    {
      brandId,
      taskName: taskFilter || null,
      hours,
      granularity,
    }
  );

  const chartData = useMemo(() => {
    return (data.internJobTimeSeries ?? []).map((bucket) => ({
      timestamp: bucket.timestamp,
      label:
        timeRange === "24h"
          ? new Date(bucket.timestamp).toLocaleTimeString("en-US", {
              hour: "numeric",
              hour12: true,
            })
          : new Date(bucket.timestamp).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }),
      success: bucket.successCount,
      failed: bucket.failureCount,
      running: bucket.runningCount,
      successRate: bucket.successRate,
    }));
  }, [data.internJobTimeSeries, timeRange]);

  const handleTimeRangeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newValue: "24h" | "7d" | null) => {
      if (newValue) {
        setTimeRange(newValue);
      }
    },
    []
  );

  const handleTaskChange = useCallback(
    (event: { target: { value: string } }) => {
      setTaskFilter(event.target.value);
    },
    []
  );

  // Theme-aware colors
  const chartColors = {
    grid: colorMode === "dark" ? "#374151" : "#e5e7eb",
    text: colorMode === "dark" ? "#9ca3af" : "#6b7280",
    tooltip: {
      bg: colorMode === "dark" ? "#1f2937" : "#ffffff",
      border: colorMode === "dark" ? "#374151" : "#e5e7eb",
    },
    success: "#10b981",
    failure: "#ef4444",
    running: "#3b82f6",
    successRate: "#8b5cf6",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 3,
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={2}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: "var(--intern-text-secondary)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.6875rem",
          }}
        >
          Job Execution Trends
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Task</InputLabel>
            <Select value={taskFilter} label="Task" onChange={handleTaskChange}>
              <MenuItem value="">All Tasks</MenuItem>
              {taskNames.map((task) => (
                <MenuItem key={task.taskName} value={task.taskName}>
                  {getShortTaskName(task.taskName)} ({task.count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={handleTimeRangeChange}
            size="small"
          >
            <ToggleButton value="24h" sx={{ px: 2 }}>
              24h
            </ToggleButton>
            <ToggleButton value="7d" sx={{ px: 2 }}>
              7d
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      <Box sx={{ height: 280 }}>
        {chartData.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "var(--intern-text-tertiary)" }}
            >
              No job data available for this time period
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 10, right: 30, left: -10, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={chartColors.grid}
                vertical={false}
              />
              <XAxis
                dataKey="label"
                stroke={chartColors.text}
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tick={{ fill: chartColors.text }}
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="left"
                stroke={chartColors.text}
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tick={{ fill: chartColors.text }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={chartColors.text}
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tick={{ fill: chartColors.text }}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: chartColors.tooltip.bg,
                  border: `1px solid ${chartColors.tooltip.border}`,
                  borderRadius: 8,
                  fontSize: "0.75rem",
                }}
                labelStyle={{ color: chartColors.text, fontWeight: 600 }}
                formatter={(value: number, name: string) => {
                  if (name === "successRate") {
                    return [`${value.toFixed(1)}%`, "Success Rate"];
                  }
                  return [value, name.charAt(0).toUpperCase() + name.slice(1)];
                }}
              />
              <Legend
                iconType="square"
                iconSize={8}
                formatter={(value) => (
                  <span
                    style={{
                      color: chartColors.text,
                      fontSize: "0.6875rem",
                    }}
                  >
                    {value === "successRate"
                      ? "Success Rate"
                      : value.charAt(0).toUpperCase() + value.slice(1)}
                  </span>
                )}
              />
              <Bar
                yAxisId="left"
                dataKey="success"
                stackId="jobs"
                fill={chartColors.success}
                radius={[0, 0, 0, 0]}
              />
              <Bar
                yAxisId="left"
                dataKey="failed"
                stackId="jobs"
                fill={chartColors.failure}
                radius={[2, 2, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="successRate"
                stroke={chartColors.successRate}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
};

export default InternJobsTimeSeriesChart;
